import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';

export default function VerificationPanel({ listId, onVerified }) {
  const { t } = useTranslation('common');
  const [estimate, setEstimate] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadEstimate = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setEstimate(await outreachApi.verificationEstimate(listId));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await outreachApi.runVerification(listId));
      setEstimate(null);
      if (onVerified) await onVerified();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const overBatchLimit = estimate && estimate.to_check > estimate.max_batch;

  return (
    <div className="verification-panel">
      <button className="unibox-refresh" onClick={loadEstimate} disabled={busy}>
        <ShieldCheck size={14} /> {t('outreach.verifyEmails')}
      </button>

      {estimate && (
        <span className="verification-estimate">
          {t('outreach.verifyEstimate', {
            toCheck: estimate.to_check,
            cached: estimate.already_cached,
            balance: estimate.balance,
          })}
          {estimate.to_check > 0 && !overBatchLimit && (
            <button className="unibox-button" onClick={run} disabled={busy}>
              {busy ? t('outreach.verifying') : t('outreach.verifyConfirm', { count: estimate.to_check })}
            </button>
          )}
          {overBatchLimit && (
            <span className="unibox-error">
              {t('outreach.verifyOverLimit', { max: estimate.max_batch })}
            </span>
          )}
        </span>
      )}

      {result && (
        <span className="verification-estimate">
          {t('outreach.verifyDone', { checked: result.checked, cached: result.from_cache })}{' '}
          {Object.entries(result.by_status).map(([status, count]) => (
            <span key={status} className="unibox-badge">
              {t(`outreach.verificationStatus.${status}`, status)}: {count}
            </span>
          ))}
          {result.balance_left !== null && ` — ${t('outreach.creditsLeft', { balance: result.balance_left })}`}
        </span>
      )}

      {error && <span className="unibox-error">{String(error)}</span>}
    </div>
  );
}
